import ButtonFooter from 'Components/Generic/Buttons/ButtonFooter';
import CancelButton from 'Components/Generic/Buttons/CancelButton';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import UndoButton from 'Components/Generic/Buttons/UndoButton';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import ABRCodeData from 'Data/ABRCodeData';
import { useAlertContext } from 'Data/context/Alerts';
import { user_roles } from 'Data/Enum';
import SiteDrawerData from 'Data/SiteDrawerData';
import joi from 'joi';
import DataManager from 'Lib/DataManager';
import { useUndoableSaveAction } from 'Lib/hooks/useUndoableSaveAction';
import { useUpdatableObject } from 'Lib/hooks/useUpdatableObject';
import { useValidation } from 'Lib/hooks/useValidation';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { isUserAuthorized } from 'Util';

let DM = new DataManager();

const ABRCodeDetails = ({ abrCodeId, abrCodeObject, ...props }) => {
  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const {
    currentObject: abrCode,
    hasBeenUpdated,
    reset: resetABRCode,
    updateField,
    originalObject: originalABRCode,
    doSave,
  } = useUpdatableObject(
    ABRCodeData.updateABRCode,
    ABRCodeData.createABRCode,
    [],
    'abr_code',
  );
  const [resetUndo, showSaveUndoToast] = useUndoableSaveAction({
    originalObject: originalABRCode,
    undoFunction: ABRCodeData.updateRecord,
    onUndo: (originalABR) => fetchABRCode(originalABR.abr_code),
    objectType: 'ABR Code',
  });

  const currentABRCodeList = ABRCodeData.getAllABRCodes() || [];

  let editable = isUserAuthorized({
    roles: [user_roles.ADMINISTRATOR],
  });

  useEffect(() => {
    DM.add(
      ABRCodeData.registerListener('BLANK_RECORD_LOADED', onABRCodeLoaded),
      ABRCodeData,
    );
    DM.add(
      ABRCodeData.registerListener('RECORD_LOADED', onABRCodeLoaded),
      ABRCodeData,
    );
    DM.add(
      ABRCodeData.registerListener('RECORD_UPDATED', onABRCodeUpdated),
      ABRCodeData,
    );
    DM.add(
      ABRCodeData.registerListener('RECORDS_DELETED', onABRCodeDeleted),
      ABRCodeData,
    );
    return () => {
      DM.clear();
    };
  }, []);

  let fields = {
    abr_code: {
      label: 'ABR Code',
      fieldSchema: joi
        .string()
        .label('ABR Code')
        .invalid(
          ...currentABRCodeList
            .filter(({ abr_code }) => originalABRCode?.abr_code !== abr_code)
            .map(({ abr_code }) => abr_code),
        )
        .insensitive()
        .required()
        .messages({
          'any.invalid': 'ABR Code already in use',
          'string.empty': 'ABR Code is required',
        }),
      onEdit: (newValue) => {
        updateField('abr_code', newValue);
      },
      isRequired: true,
    },
    description: {
      label: 'Description',
      onEdit: (newValue) => {
        updateField('description', newValue);
      },
      isRequired: true,
    },
  };

  let { resetValidation, getValidatedDetailTable, ValidatedSaveButton } =
    useValidation(fields, abrCode);

  useEffect(() => {
    if (!abrCodeId) {
      ABRCodeData.getBlank();
    }
  }, []);

  useEffect(() => {
    if (abrCodeId) {
      fetchABRCode(abrCodeId);
    }
  }, [abrCodeId]);

  useEffect(() => {
    if (!editable) {
      resetABRCode(abrCodeObject);
    } else if (abrCodeObject?.abr_code) {
      fetchABRCode(abrCodeObject.abr_code);
    }
  }, [abrCodeObject]);

  const fetchABRCode = (abr_code) => {
    ABRCodeData.getRecord(abr_code);
  };

  const onABRCodeLoaded = (payload) => {
    if (payload) {
      resetABRCode(payload.abr_code ? payload : {});
      resetUndo(() => fetchABRCode(payload.abr_code));
      resetValidation();
    }
  };

  const onABRCodeUpdated = (payload) => {
    if (payload) {
      showSaveUndoToast();
    }
  };

  let delCancelButton =
    editable &&
    (abrCodeId && editable ? (
      <DeleteButton
        onDeleteItem={() => {
          deleteClicked();
        }}
        tooltipText={'Delete ABR Code'}
      />
    ) : (
      <CancelButton onCancel={SiteDrawerData.close} />
    ));

  const deleteABRCode = () => {
    ABRCodeData.deleteABRCode(abrCodeId);
  };

  const deleteClicked = () => {
    openDeleteDialog({
      objectType: 'ABR Code',
      onConfirmDelete: deleteABRCode,
    });
  };

  const onABRCodeDeleted = () => {
    showDeletedToast('ABR Code');
  };

  const undoAction = () => {
    fetchABRCode(abrCode.abr_code);
    resetValidation();
  };

  const undoButton = editable && abrCode.abr_code && hasBeenUpdated && (
    <UndoButton clickHandler={undoAction} />
  );

  const saveButton = editable && hasBeenUpdated && (
    <ValidatedSaveButton doSave={doSave} />
  );

  const footer = (
    <ButtonFooter>
      {undoButton}
      {saveButton}
    </ButtonFooter>
  );

  return (
    abrCode && (
      <div
        style={{
          display: 'flex',
          gap: 10,
          flexDirection: 'column',
          maxWidth: 600,
        }}>
        <EditableDetailPanel
          editable={editable}
          renderChild={getValidatedDetailTable}
          footer={footer}
          {...props}
        />
        {delCancelButton}
      </div>
    )
  );
};

ABRCodeDetails.propTypes = {
  abrCodeId: PropTypes.string,
  abrCodeObject: PropTypes.object,
  editable: PropTypes.bool,
};

export default ABRCodeDetails;
